// CSV import modal — same shape as the 1PIF importer.

import { el, clear } from './dom.js';
import { csvToEntries } from '../export/csv-codec.js';
import { openSheet } from './sheet.js';

export function openCsvImportDialog({ vault, autoLock, onDone }) {
  autoLock?.pause?.();

  const main = el('main');
  const footer = el('div', { class: 'form-actions' });
  const body = el('div', { class: 'import-body' }, [main, footer]);

  const sheetHandle = openSheet({
    kind: 'fullscreen',
    title: 'Import from CSV',
    body,
    onDismiss: () => autoLock?.resume?.(),
  });

  const close = () => sheetHandle.close();

  showPicker();

  function showPicker() {
    clear(main);
    const drop = el('div', { class: 'drop-zone' }, [
      el('div', {}, ['Drop a .csv file here']),
      el('div', { style: { fontSize: '12px', marginTop: '6px' } }, ['or click to choose']),
    ]);
    const fileInput = el('input', { type: 'file', accept: '.csv,text/csv', style: { display: 'none' }, onchange: (e) => {
      const f = e.target.files?.[0]; if (f) readFile(f);
    } });
    drop.addEventListener('click', () => fileInput.click());
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('over');
      const f = e.dataTransfer?.files?.[0]; if (f) readFile(f);
    });
    main.append(drop, fileInput);

    clear(footer);
    footer.append(el('button', { class: 'ghost', onclick: close }, ['Cancel']));
  }

  async function readFile(file) {
    clear(main);
    main.append(el('div', {}, [`Parsing ${file.name}…`]));
    let text;
    try { text = await file.text(); }
    catch (err) { showError(`Could not read file: ${err.message ?? err}`); return; }
    let result;
    try { result = csvToEntries(text); }
    catch (err) { showError(`Parse failed: ${err.message ?? err}`); return; }
    showPreview(result);
  }

  function showPreview(result) {
    clear(main);
    const lines = Object.entries(result.summary.byType)
      .map(([type, n]) => `${n} ${type}${n === 1 ? '' : 's'}`)
      .join(', ');
    main.append(
      el('p', {}, [`Found ${result.summary.total} rows. Will import ${result.entries.length}: ${lines || '(none)'}.`]),
    );
    const notes = [];
    if (result.summary.skipped) notes.push(`${result.summary.skipped} blank rows skipped`);
    if (result.summary.errors) notes.push(`${result.summary.errors} rows with errors`);
    if (notes.length) main.append(el('p', { style: { color: 'var(--muted)', fontSize: '13px' } }, [notes.join(' · ')]));
    if (result.errors.length) {
      main.append(el('details', { style: { marginTop: '10px' } }, [
        el('summary', { style: { cursor: 'pointer', color: 'var(--muted)' } }, [`${result.errors.length} errors`]),
        el('ul', { style: { color: 'var(--muted)', fontSize: '13px', lineHeight: '1.5', maxHeight: '180px', overflowY: 'auto' } },
          result.errors.slice(0, 200).map((er) => el('li', {}, [`Row ${er.row}: ${er.message}`])),
        ),
      ]));
    }

    clear(footer);
    footer.append(
      el('button', { class: 'ghost', onclick: close }, ['Cancel']),
      el('button', { class: 'primary', disabled: result.entries.length === 0, onclick: async () => {
        footer.querySelectorAll('button').forEach((b) => (b.disabled = true));
        try {
          let inserted = 0;
          for (const e of result.entries) { await vault.upsert(e); inserted++; }
          close();
          onDone?.({ inserted });
        } catch (err) {
          showError(err.message ?? String(err));
        }
      } }, [`Import ${result.entries.length} entries`]),
    );
  }

  function showError(msg) {
    clear(main);
    main.append(el('div', { class: 'error', style: { color: 'var(--danger)' } }, [msg]));
    clear(footer);
    footer.append(el('button', { onclick: showPicker }, ['Try again']));
  }
}
